import type { ColumnFiltersState } from '@tanstack/react-table';
import searchAtomPair, { type SearchParams } from 'atoms/search-atom';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  ExpenseScenarioAggregations,
  ExpenseScenarioFacets,
  ExpenseScenarioResult,
} from './types';

export interface DateRangeFilter {
  start?: string;
  end?: string;
}

export interface ExpenseScenarioParams extends SearchParams {
  createdBy?: string[];
  effectiveDate?: DateRangeFilter;
  createdAt?: DateRangeFilter;
  updatedAt?: DateRangeFilter;
}

export const DEFAULT_PARAMS: ExpenseScenarioParams = {
  page: 1,
  pageSize: 100,
  sortAttribute: 'created_at',
  sortDirection: 'DESC',
};

// Maps DataTable column ids (camelCase accessorKeys) to the BE's snake_case
// sortable fields (Query::ExpenseScenarios::SORTABLE_FIELDS). Columns absent
// here are not sortable on the BE; callers fall back to the default sort.
export const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  effectiveDate: 'effective_date',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
};

export const createExpenseScenariosSearchAtom = () =>
  searchAtomPair<
    ExpenseScenarioResult,
    ExpenseScenarioParams,
    ExpenseScenarioFacets,
    ExpenseScenarioAggregations
  >({ href: '/queries/expense_scenarios' }, DEFAULT_PARAMS);

interface ClonedExpenseScenario {
  id: number;
  href: string;
}

/**
 * POSTs to the BE clone endpoint for a scenario by id and returns the cloned
 * scenario's identity. Mirrors the differential-scenario clone-atom pattern.
 * @param {number} id - The source scenario id
 * @return {ClonedExpenseScenario} The cloned scenario (id + href)
 */
export const [cloneScenarioAtom, cloneScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, _set, id: number): Promise<ClonedExpenseScenario> => {
      const response = await request.post<ClonedExpenseScenario, unknown>(
        `/expense_scenarios/${id}/clone`,
        {},
        new AbortController()
      );

      if (response.type !== 'success') {
        throw new Error('Failed to clone expense scenario');
      }
      return response.data;
    }
  );

const toDateRange = (value: unknown): DateRangeFilter | undefined => {
  if (!Array.isArray(value)) return undefined;
  const [start, end] = value as [string | null, string | null];
  if (!start && !end) return undefined;
  return { start: start ?? undefined, end: end ?? undefined };
};

export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<ExpenseScenarioParams> => {
  const params: Partial<ExpenseScenarioParams> = {
    createdBy: undefined,
    effectiveDate: undefined,
    createdAt: undefined,
    updatedAt: undefined,
  };

  for (const filter of filters) {
    switch (filter.id) {
      case 'createdBy':
        params.createdBy = filter.value as string[];
        break;
      case 'effectiveDate':
        params.effectiveDate = toDateRange(filter.value);
        break;
      case 'createdAt':
        params.createdAt = toDateRange(filter.value);
        break;
      case 'updatedAt':
        params.updatedAt = toDateRange(filter.value);
        break;
    }
  }

  return params;
};
